export const getMiddleSemesterLabel = (settings) => {
  const preference = String(settings?.preferences?.semesterMiddleLabel || 'spring').trim().toLowerCase();
  return preference === 'winter' ? 'Winter' : 'Spring';
};

export const getSemesterGroupKey = (semester) => {
  const normalized = String(semester || '').trim().toLowerCase();

  if (!normalized) return 'unknown';

  if (normalized.includes('fall') || normalized.includes('autumn')) return 'fall';
  if (normalized.includes('spring') || normalized.includes('winter')) return 'middle';
  if (normalized.includes('summer')) return 'summer';

  return normalized;
};

export const getSemesterSortOrder = (semester) => {
  const groupKey = getSemesterGroupKey(semester);

  if (groupKey === 'fall') return 0;
  if (groupKey === 'middle') return 1;
  if (groupKey === 'summer') return 2;

  return 99;
};

export const getSemesterTimelineSortKey = (semester, year) => {
  const normalizedYear = Number(year) || 0;
  const groupKey = getSemesterGroupKey(semester);

  const termOffset = groupKey === 'fall'
    ? 0.5
    : groupKey === 'summer'
      ? 0.25
      : 0;

  return normalizedYear + termOffset;
};

export const getSemesterDisplayLabel = (semester, settings) => {
  const groupKey = getSemesterGroupKey(semester);

  if (groupKey === 'middle') {
    return getMiddleSemesterLabel(settings);
  }

  if (groupKey === 'fall') return 'Fall';
  if (groupKey === 'summer') return 'Summer';

  const rawSemester = String(semester || '').trim();
  if (!rawSemester) return '';

  return rawSemester.charAt(0).toUpperCase() + rawSemester.slice(1).toLowerCase();
};

export const getAcademicSemesterOptions = (settings) => ([
  'Fall',
  getMiddleSemesterLabel(settings),
  'Summer'
]);

export const getCurrentAcademicSemesterGroup = (date = new Date()) => {
  const month = date.getMonth() + 1;

  if (month >= 8 && month <= 12) return 'fall';
  if (month >= 1 && month <= 4) return 'middle';
  if (month >= 5 && month <= 7) return 'summer';

  return 'middle';
};

export const getCurrentAcademicSemesterLabel = (date, settings) => {
  const currentGroup = getCurrentAcademicSemesterGroup(date);
  if (currentGroup === 'fall') return 'Fall';
  if (currentGroup === 'summer') return 'Summer';
  return getMiddleSemesterLabel(settings);
};

const normalizeAssignmentStatus = (status) => String(status || '').trim().toLowerCase().replace(/_/g, '-');

const toTimestamp = (value) => {
  if (!value) return null;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getAssignmentActivityTimestamp = (assignment) => {
  return toTimestamp(
    assignment?.updatedAt ||
    assignment?.completedAt ||
    assignment?.completed_at ||
    assignment?.submittedAt ||
    assignment?.createdAt ||
    assignment?.assignedAt ||
    assignment?.due_date
  );
};

const getCourseTermBucketKey = (course) => {
  const semesterGroup = getSemesterGroupKey(course?.semester);
  const year = Number(course?.year) || 0;
  return `${year}-${semesterGroup}`;
};

const createTermBucket = (course) => ({
  semester: course?.semester || '',
  semesterGroup: getSemesterGroupKey(course?.semester),
  year: Number(course?.year) || 0,
  courses: [course],
  lastCompletedAt: null,
  lastActivityAt: null,
  activeAssignmentCount: 0,
  totalAssignmentCount: 0,
  sortKey: getSemesterTimelineSortKey(course?.semester, course?.year)
});

const compareTermBuckets = (a, b) => {
  if ((a.lastCompletedAt || 0) !== (b.lastCompletedAt || 0)) {
    return (b.lastCompletedAt || 0) - (a.lastCompletedAt || 0);
  }

  if ((a.lastActivityAt || 0) !== (b.lastActivityAt || 0)) {
    return (b.lastActivityAt || 0) - (a.lastActivityAt || 0);
  }

  if (a.activeAssignmentCount !== b.activeAssignmentCount) {
    return b.activeAssignmentCount - a.activeAssignmentCount;
  }

  if (a.totalAssignmentCount !== b.totalAssignmentCount) {
    return b.totalAssignmentCount - a.totalAssignmentCount;
  }

  return b.sortKey - a.sortKey;
};

export const getMostRelevantAcademicTerm = (courses = [], assignments = [], settings) => {
  const courseMap = new Map();
  const buckets = new Map();

  (courses || []).forEach(course => {
    const courseId = course?._id || course?.id;
    if (!courseId) return;

    const key = getCourseTermBucketKey(course);
    courseMap.set(String(courseId), course);

    if (!buckets.has(key)) {
      buckets.set(key, createTermBucket(course));
      return;
    }

    const bucket = buckets.get(key);
    bucket.courses.push(course);
    bucket.sortKey = Math.max(bucket.sortKey, getSemesterTimelineSortKey(course?.semester, course?.year));
  });

  (assignments || []).forEach(assignment => {
    const assignmentCourseId = typeof assignment?.course === 'object'
      ? assignment.course?._id || assignment.course?.id
      : assignment?.course;

    if (!assignmentCourseId) return;

    const course = courseMap.get(String(assignmentCourseId));
    if (!course) return;

    const key = getCourseTermBucketKey(course);
    if (!buckets.has(key)) {
      buckets.set(key, createTermBucket(course));
    }

    const bucket = buckets.get(key);
    bucket.totalAssignmentCount += 1;

    const activityTimestamp = getAssignmentActivityTimestamp(assignment);
    if (activityTimestamp !== null) {
      bucket.lastActivityAt = Math.max(bucket.lastActivityAt || 0, activityTimestamp);
    }

    if (normalizeAssignmentStatus(assignment?.status) !== 'completed') {
      bucket.activeAssignmentCount += 1;
      return;
    }

    if (activityTimestamp !== null) {
      bucket.lastCompletedAt = Math.max(bucket.lastCompletedAt || 0, activityTimestamp);
    }
  });

  const selectedBucket = Array.from(buckets.values()).sort(compareTermBuckets)[0];
  if (!selectedBucket) {
    return {
      semester: '',
      semesterGroup: 'unknown',
      year: 0,
      courses: [],
      displaySemester: '',
      termName: ''
    };
  }

  return {
    ...selectedBucket,
    displaySemester: getSemesterDisplayLabel(selectedBucket.semester, settings),
    termName: `${getSemesterDisplayLabel(selectedBucket.semester, settings)} ${selectedBucket.year}`
  };
};
